import React from "react";
import { Typography } from "@mui/material";
import DocumentCard from "@components/DocumentCard/DocumentCard";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import type { SideDrawerAttachments } from "@services/movement-services/types";
import { useTranslation } from "react-i18next";
import type { MovementServiceStatusDetails } from "../types";

const LANG_BASE = "vessel-call.movement-services.preview.marine-information";
const FIELD_LABEL_CLASS = "w-36 shrink-0 font-normal text-[#646A77]";
const VALUE_CLASS = "min-w-0 flex-1 font-semibold text-[#111827]";

interface MarineInformationSectionProps {
  title: string;
  decision?: MovementServiceStatusDetails;
  testId: string;
  onDownload: (attachment: SideDrawerAttachments) => void;
  onPreview: (attachment: SideDrawerAttachments) => void;
}

const MarineInformationSection: React.FC<MarineInformationSectionProps> = ({
  title,
  decision,
  testId,
  onDownload,
  onPreview,
}) => {
  const { t } = useTranslation();
  if (!decision) return null;

  const hasExternalNotes = Boolean(decision.externalNotes?.trim());
  const hasExternalAttachments = Boolean(decision.externalAttachments?.length);
  const hasInternalNotes = Boolean(decision.internalNotes?.trim());
  const hasInternalAttachments = Boolean(decision.internalAttachments?.length);
  const hasExternalInformation = hasExternalNotes || hasExternalAttachments;
  const hasInternalInformation = hasInternalNotes || hasInternalAttachments;

  return (
    <Paper variant="outlined">
      <div className="flex flex-col gap-3" data-testid={testId}>
        <Typography
          component="h3"
          variant="subtitle1"
          className="font-bold"
          sx={{ fontWeight: 600, color: "#111827" }}
        >
          {title}
        </Typography>
        <div className="flex flex-col gap-4">
          {hasExternalInformation && (
            <div className="flex flex-col gap-4">
              {hasExternalNotes && (
                <PaperItem
                  testId={`${testId}-external-notes`}
                  translate={t(`${LANG_BASE}.notes-to-agent`)}
                  text={decision.externalNotes}
                  textStyles={VALUE_CLASS}
                  tranStyle={FIELD_LABEL_CLASS}
                  wrapperStyle="min-w-0 gap-4"
                  labelClassName={FIELD_LABEL_CLASS}
                  valueClassName={VALUE_CLASS}
                />
              )}
              {hasExternalAttachments && (
                <Typography
                  component="div"
                  variant="body1"
                  data-testid={`${testId}-attachments`}
                  className={`min-w-0 grid grid-cols-2 gap-4${hasExternalNotes ? " border-t border-[#F1F2F4] pt-3" : ""}`}
                >
                  {decision.externalAttachments?.map((attachment, attachmentIndex) => (
                    <DocumentCard
                      key={`${attachment.url}-${attachmentIndex}`}
                      fileName={attachment.originalFileName || "-"}
                      onDownload={() => onDownload(attachment)}
                      onPreview={() => onPreview(attachment)}
                    />
                  ))}
                </Typography>
              )}
            </div>
          )}
          {hasInternalInformation && (
            <div
              className={`flex flex-col gap-4${hasExternalInformation ? " border-t border-[#F1F2F4] pt-3" : ""}`}
            >
              {hasInternalNotes && (
                <PaperItem
                  testId={`${testId}-internal-notes`}
                  translate={t(`${LANG_BASE}.internal-notes`)}
                  text={decision.internalNotes}
                  textStyles={VALUE_CLASS}
                  tranStyle={FIELD_LABEL_CLASS}
                  wrapperStyle="min-w-0 gap-4"
                  labelClassName={FIELD_LABEL_CLASS}
                  valueClassName={VALUE_CLASS}
                />
              )}
              {hasInternalAttachments && (
                <Typography
                  component="div"
                  variant="body1"
                  data-testid={`${testId}-internal-attachments`}
                  className={`min-w-0 grid grid-cols-2 gap-4${hasInternalNotes ? " border-t border-[#F1F2F4] pt-3" : ""}`}
                >
                  {decision.internalAttachments?.map((attachment, attachmentIndex) => (
                    <DocumentCard
                      key={`${attachment.url}-${attachmentIndex}`}
                      fileName={attachment.originalFileName || "-"}
                      onDownload={() => onDownload(attachment)}
                      onPreview={() => onPreview(attachment)}
                    />
                  ))}
                </Typography>
              )}
            </div>
          )}
        </div>
      </div>
    </Paper>
  );
};

export default MarineInformationSection;
